import type {
  AddGuestsToBookingDto,
  AddUsersToPropertyDto,
  AnalyticsBookingsBySourcesDto,
  AnalyticsBookingsBySourcesRes,
  AnalyticsOccupancyRateDto,
  AnalyticsOccupancyRateRes,
  AnalyticsRoomsDto,
  AnalyticsRoomsRes,
  AnalyticsTotalDto,
  AnalyticsTotalRes,
  BookingRequestWithProperty,
  BookingWithBookedRooms,
  BookingWithProperty,
  BookingWithRoomsAndProperty,
  CreateBookedRoomDto,
  CreateBookingDto,
  CreateCustomerDto,
  CreateGuestDto,
  CreateNoteDto,
  CreatePropertyDto,
  CreateRoomDto,
  CreateRoomTypeDto,
  CreateUserDto,
  CreateWorkplaceDto,
  CreateZoneDto,
  DownloadContractRes,
  DownloadNoteFileRes,
  EmailDto,
  GetAuctionDto,
  GetAuctionRes,
  GetBookingRequestDto,
  GetCustomerResponse,
  GetPropertyRes,
  IdDto,
  LeadsBySourcesDto,
  LeadsBySourcesRes,
  LeadsByZonesDto,
  LeadsByZonesRes,
  LeadsTotalDto,
  LeadsTotalRes,
  ListAuctionsDto,
  ListAuctionsRes,
  ListBookingPaymentsDto,
  ListBookingRequestsDto,
  ListBookingsByGuestDto,
  ListBookingsByPropertyDto,
  ListBookingsByRoomTypeDto,
  ListBookingsDto,
  ListContractsDto,
  ListCustomersDto,
  ListGuestsDto,
  ListNotesDto,
  ListNotesRes,
  ListPropertiesDto,
  ListPropertyImagesDto,
  ListRes,
  ListRoomsDto,
  ListRoomTypesDto,
  ListUsersByPropertyDto,
  ListUsersByPropertyRes,
  ListUsersDto,
  ListUsersRes,
  ListWorkplacesDto,
  ListZonesDto,
  LoginDto,
  RemoveBookedRoomDto,
  RemoveGuestFromBookingDto,
  RemoveUserFromPropertyDto,
  TenantUser,
  UpdateBookedRoomDto,
  UpdateBookingDto,
  UpdateBookingPaymentDto,
  UpdateContractDto,
  UpdateCustomerDto,
  UpdateGuestDto,
  UpdateNoteDto,
  UpdateNoteFileDto,
  UpdatePropertyDto,
  UpdatePropertyImageDto,
  UpdateRoomDto,
  UpdateRoomTypeDto,
  UpdateUserDto,
  UpdateWorkplaceDto,
  UpdateZoneDto,
  UploadContractDto,
  UploadNoteFilesDto,
  UploadPropertyImagesDto,
  UserWithWorkplaces,
} from '@kolumboos/data';
import type {
  Booking,
  BookingPayment,
  Contract,
  Customer,
  Guest,
  Note,
  NoteFile,
  Property,
  PropertyImage,
  Room,
  RoomType,
  User,
  Workplace,
  Zone,
} from '@prisma/client';
import Axios from 'axios';
import { getApiHost } from '../config';
import { getFBAuthToken } from '../services/firebase.service';
import { isDateString } from '../utils/dates.utils';

const dateReviver = (_: string, value: any) =>
  typeof value === 'string' && value && isDateString(value) ? new Date(value) : value;

const axios = Axios.create({
  baseURL: getApiHost(),
  transformResponse: [(data) => (data ? JSON.parse(data, dateReviver) : null)],
});

interface ApiMethods {
  // analytics
  'analytics/propertiesTotal': (dto: AnalyticsTotalDto) => AnalyticsTotalRes;
  'analytics/bookingsTotal': (dto: AnalyticsTotalDto) => AnalyticsTotalRes;
  'analytics/guestsTotal': (dto: AnalyticsTotalDto) => AnalyticsTotalRes;
  'analytics/roomsRevenue': (dto: AnalyticsTotalDto) => AnalyticsTotalRes;
  'analytics/roomsDataByMonths': (dto: AnalyticsRoomsDto) => AnalyticsRoomsRes;
  'analytics/occupancyRate': (dto: AnalyticsOccupancyRateDto) => AnalyticsOccupancyRateRes;
  'analytics/bookingsBySources': (
    dto: AnalyticsBookingsBySourcesDto
  ) => AnalyticsBookingsBySourcesRes;
  'analytics/leadsTotal': (dto: LeadsTotalDto) => LeadsTotalRes;
  'analytics/leadsBySources': (dto: LeadsBySourcesDto) => LeadsBySourcesRes;
  'analytics/leadsByZones': (dto: LeadsByZonesDto) => LeadsByZonesRes;

  // auth
  'auth/login': (dto: LoginDto) => TenantUser;
  'auth/requestSignUpEmail': (dto: EmailDto) => void;
  'auth/resetPassword': (dto: EmailDto) => void;

  // auctions
  'auctions/list': (dto: ListAuctionsDto) => ListAuctionsRes;
  'auctions/get': (dto: GetAuctionDto) => GetAuctionRes;

  // bookings
  'bookings/list': (dto: ListBookingsDto) => ListRes<BookingWithProperty>;
  'bookings/listByProperty': (dto: ListBookingsByPropertyDto) => ListRes<Booking>;
  'bookings/listByRoomType': (dto: ListBookingsByRoomTypeDto) => ListRes<BookingWithBookedRooms>;
  'bookings/listByGuest': (dto: ListBookingsByGuestDto) => ListRes<BookingWithProperty>;
  'bookings/get': (dto: IdDto) => BookingWithRoomsAndProperty;
  'bookings/create': (dto: CreateBookingDto) => Booking;
  'bookings/update': (dto: UpdateBookingDto) => Booking;
  'bookings/confirm': (dto: IdDto) => void;
  'bookings/cancel': (dto: IdDto) => void;
  'bookings/archive': (dto: IdDto) => void;
  'bookings/restore': (dto: IdDto) => void;
  'bookings/delete': (dto: IdDto) => void;
  // booked rooms
  'bookedRooms/create': (dto: CreateBookedRoomDto) => void;
  'bookedRooms/update': (dto: UpdateBookedRoomDto) => void;
  'bookedRooms/remove': (dto: RemoveBookedRoomDto) => void;
  // bookings payments
  'bookingPayments/list': (dto: ListBookingPaymentsDto) => ListRes<BookingPayment>;
  'bookingPayments/update': (dto: UpdateBookingPaymentDto) => BookingPayment;
  // bookings guests
  'bookings/addGuests': (dto: AddGuestsToBookingDto) => void;
  'bookings/removeGuest': (dto: RemoveGuestFromBookingDto) => void;

  // contracts
  'contracts/list': (dto: ListContractsDto) => ListRes<Contract>;
  'contracts/download': (dto: IdDto) => DownloadContractRes;
  'contracts/upload': (dto: UploadContractDto) => Contract;
  'contracts/update': (dto: UpdateContractDto) => Contract;
  'contracts/delete': (dto: IdDto) => void;

  // customers
  'customers/list': (dto: ListCustomersDto) => ListRes<Customer>;
  'customers/get': (dto: IdDto) => GetCustomerResponse;
  'customers/create': (dto: CreateCustomerDto) => Customer;
  'customers/update': (dto: UpdateCustomerDto) => Customer;
  'customers/promote': (dto: IdDto) => User;
  'customers/delete': (dto: IdDto) => void;

  // guests
  'guests/list': (dto: ListGuestsDto) => ListRes<Guest>;
  'guests/get': (dto: IdDto) => Guest;
  'guests/create': (dto: CreateGuestDto) => Guest;
  'guests/update': (dto: UpdateGuestDto) => Guest;
  'guests/delete': (dto: IdDto) => void;

  // notes
  'notes/list': (dto: ListNotesDto) => ListNotesRes;
  'notes/create': (dto: CreateNoteDto) => Note;
  'notes/update': (dto: UpdateNoteDto) => Note;
  'notes/delete': (dto: IdDto) => void;
  // notes files
  'notesFiles/download': (dto: IdDto) => DownloadNoteFileRes;
  'notesFiles/upload': (dto: UploadNoteFilesDto) => void;
  'notesFiles/update': (dto: UpdateNoteFileDto) => NoteFile;
  'notesFiles/delete': (dto: IdDto) => void;

  // properties
  'properties/list': (dto: ListPropertiesDto) => ListRes<Property>;
  'properties/get': (dto: IdDto) => GetPropertyRes;
  'properties/create': (dto: CreatePropertyDto) => Property;
  'properties/update': (dto: UpdatePropertyDto) => Property;
  'properties/archive': (dto: IdDto) => void;
  'properties/restore': (dto: IdDto) => void;
  'properties/delete': (dto: IdDto) => void;
  // properties users
  'properties/addUsers': (dto: AddUsersToPropertyDto) => void;
  'properties/removeUser': (dto: RemoveUserFromPropertyDto) => void;
  // properties images
  'propertiesImages/list': (dto: ListPropertyImagesDto) => ListRes<PropertyImage>;
  'propertiesImages/upload': (dto: UploadPropertyImagesDto) => void;
  'propertiesImages/update': (dto: UpdatePropertyImageDto) => PropertyImage;
  'propertiesImages/delete': (dto: IdDto) => void;

  // rooms types
  'roomTypes/list': (dto: ListRoomTypesDto) => ListRes<RoomType>;
  'roomTypes/create': (dto: CreateRoomTypeDto) => RoomType;
  'roomTypes/update': (dto: UpdateRoomTypeDto) => RoomType;
  'roomTypes/archive': (dto: IdDto) => void;
  'roomTypes/restore': (dto: IdDto) => void;
  'roomTypes/delete': (dto: IdDto) => void;

  // rooms
  'rooms/list': (dto: ListRoomsDto) => ListRes<Room>;
  'rooms/create': (dto: CreateRoomDto) => Room;
  'rooms/update': (dto: UpdateRoomDto) => Room;
  'rooms/delete': (dto: IdDto) => void;

  // users
  'users/list': (dto: ListUsersDto) => ListUsersRes;
  'users/listByProperty': (dto: ListUsersByPropertyDto) => ListUsersByPropertyRes;
  'users/get': (dto: IdDto) => UserWithWorkplaces;
  'users/create': (dto: CreateUserDto) => User;
  'users/update': (dto: UpdateUserDto) => User;
  'users/delete': (dto: IdDto) => void;

  // workplaces
  'workplaces/list': (dto: ListWorkplacesDto) => ListRes<Workplace>;
  'workplaces/create': (dto: CreateWorkplaceDto) => Workplace;
  'workplaces/update': (dto: UpdateWorkplaceDto) => Workplace;
  'workplaces/delete': (dto: IdDto) => void;

  // zones
  'zones/list': (dto: ListZonesDto) => ListRes<Zone>;
  'zones/create': (dto: CreateZoneDto) => Zone;
  'zones/update': (dto: UpdateZoneDto) => Zone;
  'zones/delete': (dto: IdDto) => void;

  // booking-requests
  'bookingRequests/list': (dto: ListBookingRequestsDto) => ListRes<BookingRequestWithProperty>;
  'bookingRequests/get': (dto: GetBookingRequestDto) => BookingRequestWithProperty;
  'bookingRequests/archive': (dto: IdDto) => void;
  'bookingRequests/restore': (dto: IdDto) => void;
  'bookingRequests/delete': (dto: IdDto) => void;
}

export type ApiMethod = keyof ApiMethods;
export type ApiMethodDto<K extends ApiMethod> = Parameters<ApiMethods[K]>[0];
export type ApiMethodRes<K extends ApiMethod> = ReturnType<ApiMethods[K]>;

axios.interceptors.request.use(async (req) => {
  if (!req.url?.includes('/auth')) {
    const token = await getFBAuthToken();
    req.headers['Authorization'] = `Bearer ${token}`;
  }

  return req;
});

const request = async <K extends ApiMethod>(
  path: K,
  data: ApiMethodDto<K>
): Promise<ApiMethodRes<K>> => {
  const res = await axios.post<ApiMethodRes<K>>('/' + path, data);
  return res.data;
};

const api = { request };

export default api;
